import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { IdProvider, unwrap } from '../common/apiCommons';
import axios from 'axios';

export enum ProjectPictureKeys {
  PROJECT_PICTURE = 'PROJECT_PICTURE',
}

export type ProjectPicture = {
  id: number;
  picture: string;
};

const queries = {
  async getProjectPicture(project: IdProvider) {
    if (!project?.id) {
      return null;
    }
    const res = await axios.get<ProjectPicture>(`/project/pic/${project.id}/`);
    return unwrap(res);
  },
};

const mutations = {
  async updateProjectPicture(project: IdProvider, image: File): Promise<void> {
    if (!project?.id) {
      return;
    }
    const formData = new FormData();
    formData.append('picture', image);
    const res = await axios({
      method: 'post',
      data: formData,
      url: `/project/add_pic/${project.id}/`,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(res);
  },
  async deleteProjectPicture(project: IdProvider): Promise<void> {
    if (!project?.id) {
      return;
    }
    const res = await axios.delete(`/project/pic/${project.id}/`);
    return unwrap(res);
  },
};

const hooks = {
  useProjectPictureQuery(
    project: IdProvider,
    options?: UseQueryOptions<ProjectPicture | null>,
  ) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [ProjectPictureKeys.PROJECT_PICTURE, project?.id],
      queryFn: () => queries.getProjectPicture(project),
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () =>
        queryClient.invalidateQueries([
          ProjectPictureKeys.PROJECT_PICTURE,
          project?.id,
        ]),
    ];
    return QueryResultAndInvalidate;
  },
  useUpdateProjectPictureMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ project, image }: { project: IdProvider; image: File }) =>
        mutations.updateProjectPicture(project, image),
      async onSuccess(_, { project }) {
        await queryClient.invalidateQueries([
          ProjectPictureKeys.PROJECT_PICTURE,
          project?.id,
        ]);
      },
    });
  },
  useDeleteProjectPictureMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (project: IdProvider) =>
        mutations.deleteProjectPicture(project),
      async onSuccess(_, project) {
        await queryClient.invalidateQueries([
          ProjectPictureKeys.PROJECT_PICTURE,
          project?.id,
        ]);
      },
    });
  },
};

const ProjectPictureApi = {
  queries,
  mutations,
  hooks,
};

export default ProjectPictureApi;
